import { useState } from 'react';
import { calcQuality, levelDv, levelSl, levelAcc, mskToday } from '../lib/alerts';
import { GoBtn } from './AlertRibbon';

const PILL = {
  g: { bg: '#C6EFCE', fg: '#276221' },
  y: { bg: '#FFF7E0', fg: '#8A6100' },
  r: { bg: '#FFC7CE', fg: '#9C0006' },
};
function Pill({ lv, children }) {
  const c = PILL[lv] || PILL.g;
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>{children}</span>;
}
const sgn = (v, d = 0) => (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v).toFixed(d);
const COLS = 'minmax(120px,1.6fr) 64px 64px 64px 62px 62px 74px 62px 70px 78px';

// units: [{ id, label, sub, groups: [{ sv, routes }] }] — по СВ (у ТМ) или по ТМ (у РМ)
export default function QualityPlanning({ units, weeks, weekNums, rwFrozen, rwLive, forecasts, freezeAt, onGo, unitTitle, partial = null }) {
  const today = mskToday();
  const started = weekNums.filter(w => String(weeks[w]?.date_from || '').slice(0, 10) <= today);
  const [sel, setSel] = useState(partial?.week ?? 'month');
  const [open, setOpen] = useState({});
  const selWeeks = sel === 'month' ? started : [sel];
  const isCurrent = (w) => partial && partial.week === w;
  const selFuture = sel !== 'month' && !started.includes(sel);

  const prevWeek = sel !== 'month' && weekNums.indexOf(sel) > 0 ? weekNums[weekNums.indexOf(sel) - 1] : null;
  const accOf = (groups, wk) => calcQuality(groups, wk, rwFrozen, rwLive, forecasts);
  const deltaOf = (groups, q) => {
    if (prevWeek == null || !q) return null;
    const p = accOf(groups, [prevWeek]);
    return p ? q.acc - p.acc : null;
  };
  const routeName = (r) => { const m = Array.isArray(r.merchandisers) ? r.merchandisers[0] : r.merchandisers; return r.is_vacancy || !m?.full_name ? 'Вакансия' : m.full_name; };
  // дети строки: у ТМ — его СВ, у СВ — маршруты; показываем только тех, у кого есть данные, от худшей точности
  const childrenOf = (groups, isSv) => {
    const list = isSv
      ? groups.flatMap(g => g.routes.map(r => ({ id: r.id, label: routeName(r), sub: r.internal_code, groups: [{ sv: g.sv, routes: [r] }], leaf: true })))
      : groups.map(g => ({ id: g.sv.id, label: g.sv.full_name, sub: `${g.routes.length} маршрутов`, groups: [g], leaf: false }));
    return list.map(c => { const q = accOf(c.groups, selWeeks); return { ...c, q, d: deltaOf(c.groups, q) }; })
      .filter(c => c.q).sort((a, b) => a.q.acc - b.q.acc);
  };
  const renderKids = (groups, isSv, depth, keyPrefix) => childrenOf(groups, isSv).flatMap(c => {
    const k = keyPrefix + '/' + c.id;
    const out = [<Row key={k} name={c.label} sub={c.sub} q={c.q} delta={c.d} depth={depth}
      expandable={!c.leaf} open={!!open[k]} onToggle={() => setOpen(o => ({ ...o, [k]: !o[k] }))} />];
    if (!c.leaf && open[k]) out.push(...renderKids(c.groups, true, depth + 1, k));
    return out;
  });

  const rows = units.map(u => { const q = calcQuality(u.groups, selWeeks, rwFrozen, rwLive, forecasts); return { u, q, d: deltaOf(u.groups, q) }; });
  const total = calcQuality(units.flatMap(u => u.groups), selWeeks, rwFrozen, rwLive, forecasts);
  const withQ = rows.filter(r => r.q).sort((a, b) => a.q.acc - b.q.acc);
  const noQ = rows.filter(r => !r.q);
  const anyDerived = rows.some(r => r.q?.derived);

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow)', marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Качество планирования</div>
        <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {['month', ...weekNums].map(w => {
            const on = sel === w;
            return (
              <button key={w} onClick={() => setSel(w)}
                title={w !== 'month' && isCurrent(w) ? 'Неделя идёт: сравнение по прошедшим дням' : ''}
                style={{ border: 0, background: on ? 'var(--accent)' : 'white', color: on ? 'white' : 'var(--text)', fontSize: 12, fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}>
                {w === 'month' ? 'Месяц' : `W${w}`}{w !== 'month' && isCurrent(w) ? ' · идёт' : ''}
              </button>
            );
          })}
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
          План — актуальный прогноз СВ
        </div>
      </div>

      {!total ? (
        <div style={{ padding: '14px 16px', fontSize: 12.5, color: 'var(--text-muted)' }}>
          {selFuture ? 'Эта неделя ещё не началась.' : 'Нет данных для сравнения: нужен загруженный факт визитов и план за выбранный период.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 690 }}>
            <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 6, padding: '8px 14px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: '1px solid var(--border)' }}>
              <div>{unitTitle}</div><div style={{ textAlign: 'right' }}>План визитов</div><div style={{ textAlign: 'right' }}>Факт визитов</div><div style={{ textAlign: 'right' }}>Откл.</div>
              <div style={{ textAlign: 'right' }}>Прогноз SL</div><div style={{ textAlign: 'right' }}>Факт SL</div><div style={{ textAlign: 'right' }}>Откл. SL</div><div style={{ textAlign: 'right' }}>Точность</div><div style={{ textAlign: 'right' }} title="Изменение точности к предыдущей неделе">{prevWeek != null ? `Δ к W${prevWeek}` : ''}</div><div />
            </div>
            {withQ.map(({ u, q, d }) => {
              const k = 'u/' + u.id;
              return (
                <div key={u.id}>
                  <Row name={u.label} sub={u.sub} q={q} delta={d} onGo={() => onGo(u.id)}
                    expandable open={!!open[k]} onToggle={() => setOpen(o => ({ ...o, [k]: !o[k] }))} />
                  {open[k] && renderKids(u.groups, unitTitle === 'СВ', 1, k)}
                </div>
              );
            })}
            {total && <Row key="total" total name="Итого" sub={`${units.length} ${unitTitle === 'СВ' ? 'СВ' : 'ТМ'}`} q={total} delta={deltaOf(units.flatMap(u => u.groups), total)} />}
          </div>
        </div>
      )}
      {noQ.length > 0 && total && (
        <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
          Нет данных за период: {noQ.map(r => r.u.label).join(', ')}
        </div>
      )}
      <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border)', background: '#FAFBFC' }}>
        Точность = 100 − |факт − план| / план. Недели без загруженного факта пропускаются.{partial ? ` Идущая неделя W${partial.week} сравнивается по ${partial.until.slice(8, 10)}.${partial.until.slice(5, 7)} включительно (дни, когда факт ещё не загружен, дают занижение).` : ''}
        Прогноз SL — из прогноза СВ{anyDerived ? '; где его нет, берётся план/заказ из снимка' : ''}.
      </div>
    </div>
  );
}

function Row({ name, sub, q, onGo, total, delta = null, depth = 0, expandable = false, open = false, onToggle }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 6, alignItems: 'center', padding: '9px 14px', fontSize: 12.5, borderBottom: '1px solid #EDF2F7', background: total ? '#F7F9FC' : 'transparent', fontWeight: total ? 700 : 400 }}>
      <div style={{ minWidth: 0, paddingLeft: depth * 16, cursor: expandable ? 'pointer' : 'default' }} onClick={expandable ? onToggle : undefined}><div style={{ fontWeight: depth ? 500 : 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{expandable ? <span style={{ fontSize: 9, color: 'var(--text-muted)', marginRight: 4 }}>{open ? '▼' : '▶'}</span> : null}{name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>{sub}</div></div>
      <div style={{ textAlign: 'right' }}>{Math.round(q.plan)}</div>
      <div style={{ textAlign: 'right' }}>{Math.round(q.fact)}</div>
      <div style={{ textAlign: 'right' }}><Pill lv={levelDv(q.dv)}>{sgn(q.dv)}%</Pill></div>
      <div style={{ textAlign: 'right' }}>{q.fSl != null ? `${q.fSl.toFixed(0)}%${q.derived ? '*' : ''}` : '—'}</div>
      <div style={{ textAlign: 'right' }}>{q.aSl != null ? `${q.aSl.toFixed(0)}%` : '—'}</div>
      <div style={{ textAlign: 'right' }}>{q.dSl != null ? <Pill lv={levelSl(q.dSl)}>{sgn(q.dSl, 1)} п.п.</Pill> : '—'}</div>
      <div style={{ textAlign: 'right' }}><Pill lv={levelAcc(q.acc)}>{q.acc}</Pill></div>
      <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color: delta == null ? 'var(--text-muted)' : delta <= -5 ? '#9C0006' : delta < 0 ? '#8A6100' : '#276221' }}>
        {delta == null ? '' : (delta > 0 ? '+' : delta < 0 ? '−' : '') + Math.abs(delta)}
      </div>
      <div style={{ textAlign: 'right' }}>{onGo && <GoBtn small onClick={onGo} />}</div>
    </div>
  );
}
